import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import {
  ArrowRight,
  BrainCircuit,
  CheckCircle2,
  Cpu,
  Database,
  Bot,
  Sparkles,
  Rocket,
  Layers3,
  Network,
  BarChart3,
  Code2,
  ShieldCheck,
  BriefcaseBusiness,
  Clock3,
  Award,
  ChevronDown,
  Zap,
  Terminal,
  Workflow,
} from "lucide-react";

import {
  softwareEngPageFirstImg,
  cyberSecurityFirstPageImg,
} from "../../assets";

import Card from "../../components/card";
import SecondCard from "../../components/courses-second-card";
import Layout from "../../components/shared/layout/layout";
import CourseDescription from "./component/course-description";

const ArtificialIntelligence = () => {

  /* =========================================================
     ANIMATION
  ========================================================= */

  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 35,
    },

    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };


  /* =========================================================
     FEATURES
  ========================================================= */

  const features = [
    {
      icon: BrainCircuit,
      title: "Think in AI",
      text:
        "Understand the foundations behind artificial intelligence, machine learning and modern intelligent systems.",
    },

    {
      icon: Code2,
      title: "Build AI Systems",
      text:
        "Move beyond theory and build practical AI applications using real tools, frameworks and datasets.",
    },

    {
      icon: Database,
      title: "Work With Data",
      text:
        "Learn how data is collected, prepared, analysed and transformed into intelligent predictions.",
    },

    {
      icon: Rocket,
      title: "Ship Real Products",
      text:
        "Turn models and AI ideas into usable applications that solve real-world problems.",
    },
  ];


  /* =========================================================
     CURRICULUM
  ========================================================= */

  const curriculum = [
    "Introduction to Artificial Intelligence",

    "Python Programming for AI",

    "Mathematics & Statistics for Machine Learning",

    "Data Analysis & Data Preparation",

    "Machine Learning Fundamentals",

    "Supervised & Unsupervised Learning",

    "Regression & Classification",

    "Clustering & Dimensionality Reduction",

    "Deep Learning Fundamentals",

    "Neural Networks",

    "Computer Vision",

    "Natural Language Processing",

    "Generative AI & Large Language Models",

    "Prompt Engineering",

    "AI APIs & Application Integration",

    "Model Evaluation & Optimization",

    "AI Ethics & Responsible AI",

    "AI Deployment & MLOps",

    "Real-World AI Capstone Projects",
  ];


  /* =========================================================
     CAREER PATHS
  ========================================================= */

  const careerPaths = [
    {
      icon: BrainCircuit,
      title: "AI Engineer",
    },

    {
      icon: Cpu,
      title: "Machine Learning Engineer",
    },

    {
      icon: BarChart3,
      title: "Data Scientist",
    },

    {
      icon: Bot,
      title: "AI Application Developer",
    },

    {
      icon: Network,
      title: "ML / AI Researcher",
    },

    {
      icon: Workflow,
      title: "AI Solutions Engineer",
    },
  ];


  /* =========================================================
     AI STACK
  ========================================================= */

  const technologies = [
    "Python",
    "NumPy",
    "Pandas",
    "Scikit-learn",
    "TensorFlow",
    "PyTorch",
    "Jupyter",
    "OpenAI APIs",
    "LLM Applications",
    "SQL",
    "Git",
    "Cloud AI",
  ];


  return (
    <Layout>

      <div className="overflow-hidden bg-white text-[#07141a]">


        {/* =====================================================
            HERO
        ===================================================== */}

        <section
          className="
            relative
            min-h-[800px]
            overflow-hidden
            bg-[#050d12]
          "
        >

          {/* ===================================================
              AMBIENT GLOW
          =================================================== */}

          <div className="pointer-events-none absolute inset-0">

            <motion.div
              animate={{
                x: [0, 80, -40, 0],
                y: [0, -50, 40, 0],
                scale: [1, 1.1, 0.95, 1],
              }}
              transition={{
                duration: 18,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="
                absolute
                -left-48
                -top-48
                h-[600px]
                w-[600px]
                rounded-full
                bg-[#59b5cd]/20
                blur-[140px]
              "
            />

            <motion.div
              animate={{
                x: [0, -60, 40, 0],
                y: [0, 50, -30, 0],
                scale: [1, 0.9, 1.1, 1],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="
                absolute
                -right-48
                top-20
                h-[600px]
                w-[600px]
                rounded-full
                bg-[#ecb32c]/10
                blur-[150px]
              "
            />

            <div
              className="
                absolute
                bottom-[-250px]
                left-1/3
                h-[550px]
                w-[550px]
                rounded-full
                bg-[#CF385D]/10
                blur-[150px]
              "
            />

          </div>


          {/* ===================================================
              GRID
          =================================================== */}

          <div
            className="
              absolute
              inset-0
              opacity-[0.055]
            "
            style={{
              backgroundImage:
                "linear-gradient(#59b5cd 1px, transparent 1px), linear-gradient(90deg, #59b5cd 1px, transparent 1px)",
              backgroundSize: "55px 55px",
            }}
          />


          {/* ===================================================
              NEURAL NODES
          =================================================== */}

          <div className="pointer-events-none absolute inset-0">

            {[
              ["12%", "22%"],
              ["82%", "18%"],
              ["73%", "72%"],
              ["23%", "78%"],
              ["55%", "35%"],
              ["90%", "55%"],
            ].map(([left, top], index) => (

              <motion.div
                key={index}
                animate={{
                  opacity: [0.25, 0.8, 0.25],
                  scale: [0.8, 1.2, 0.8],
                }}
                transition={{
                  duration: 3 + index,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="
                  absolute
                  h-2
                  w-2
                  rounded-full
                  bg-[#59b5cd]
                  shadow-[0_0_20px_rgba(89,181,205,.8)]
                "
                style={{
                  left,
                  top,
                }}
              />

            ))}

          </div>


          {/* ===================================================
              CONTENT
          =================================================== */}

          <div className="relative mx-auto max-w-7xl px-6 py-24 lg:py-32">

            <div className="grid items-center gap-16 lg:grid-cols-2">


              {/* =================================================
                  LEFT
              ================================================= */}

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
              >

                <div
                  className="
                    mb-7
                    inline-flex
                    items-center
                    gap-2
                    rounded-full
                    border
                    border-[#59b5cd]/30
                    bg-[#59b5cd]/10
                    px-4
                    py-2
                    text-sm
                    font-semibold
                    text-[#59b5cd]
                  "
                >

                  <Sparkles size={16} />

                  Zulfah Academy · Artificial Intelligence

                </div>


                <h1
                  className="
                    text-5xl
                    font-black
                    leading-[0.96]
                    tracking-tight
                    text-white
                    sm:text-6xl
                    lg:text-[76px]
                  "
                >

                  Understand.

                  <br />

                  <span className="text-[#59b5cd]">
                    Build.
                  </span>

                  <br />

                  <span className="text-[#ecb32c]">
                    Intelligently.
                  </span>

                </h1>


                <p
                  className="
                    mt-8
                    max-w-xl
                    text-lg
                    leading-relaxed
                    text-white/60
                    md:text-xl
                  "
                >
                  Learn how artificial intelligence works,
                  build intelligent applications and develop
                  the practical skills needed to participate
                  in the AI-powered economy.
                </p>


                {/* CTA */}

                <div className="mt-9 flex flex-wrap gap-4">

                  <Link to="/get-started">

                    <motion.button
                      whileHover={{
                        scale: 1.03,
                      }}
                      whileTap={{
                        scale: 0.97,
                      }}
                      className="
                        group
                        flex
                        items-center
                        gap-3
                        rounded-xl
                        bg-[#ecb32c]
                        px-7
                        py-4
                        font-bold
                        text-[#07141a]
                        shadow-lg
                        shadow-[#ecb32c]/20
                      "
                    >

                      Start Learning AI

                      <ArrowRight
                        size={20}
                        className="
                          transition
                          group-hover:translate-x-1
                        "
                      />

                    </motion.button>

                  </Link>


                  <a href="#curriculum">

                    <button
                      className="
                        flex
                        items-center
                        gap-2
                        rounded-xl
                        border
                        border-white/15
                        bg-white/5
                        px-7
                        py-4
                        font-semibold
                        text-white
                        transition
                        hover:bg-white/10
                      "
                    >

                      Explore Curriculum

                      <ChevronDown size={18} />

                    </button>

                  </a>

                </div>


                {/* stats */}

                <div
                  className="
                    mt-12
                    flex
                    flex-wrap
                    gap-8
                    border-t
                    border-white/10
                    pt-8
                  "
                >

                  <div>

                    <p className="text-2xl font-bold text-white">
                      AI First
                    </p>

                    <p className="text-sm text-white/40">
                      Practical Curriculum
                    </p>

                  </div>


                  <div>

                    <p className="text-2xl font-bold text-white">
                      Python
                    </p>

                    <p className="text-sm text-white/40">
                      Core Language
                    </p>

                  </div>


                  <div>

                    <p className="text-2xl font-bold text-white">
                      Real Projects
                    </p>

                    <p className="text-sm text-white/40">
                      Portfolio Ready
                    </p>

                  </div>

                </div>

              </motion.div>


              {/* =================================================
                  AI VISUAL
              ================================================= */}

              <motion.div
                initial={{
                  opacity: 0,
                  scale: 0.9,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                }}
                transition={{
                  duration: 0.9,
                }}
                className="relative"
              >

                {/* Main AI panel */}

                <div
                  className="
                    relative
                    z-10
                    overflow-hidden
                    rounded-[32px]
                    border
                    border-white/10
                    bg-white/[0.04]
                    p-4
                    shadow-2xl
                    backdrop-blur-xl
                  "
                >

                  <div
                    className="
                      relative
                      min-h-[470px]
                      overflow-hidden
                      rounded-[25px]
                      bg-[#07141a]
                    "
                  >

                    {/* circular neural rings */}

                    <div
                      className="
                        absolute
                        left-1/2
                        top-1/2
                        h-[360px]
                        w-[360px]
                        -translate-x-1/2
                        -translate-y-1/2
                        rounded-full
                        border
                        border-[#59b5cd]/20
                      "
                    />

                    <div
                      className="
                        absolute
                        left-1/2
                        top-1/2
                        h-[260px]
                        w-[260px]
                        -translate-x-1/2
                        -translate-y-1/2
                        rounded-full
                        border
                        border-[#ecb32c]/20
                      "
                    />


                    {/* AI brain */}

                    <motion.div
                      animate={{
                        scale: [1, 1.05, 1],
                        boxShadow: [
                          "0 0 30px rgba(89,181,205,.2)",
                          "0 0 70px rgba(89,181,205,.5)",
                          "0 0 30px rgba(89,181,205,.2)",
                        ],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                      }}
                      className="
                        absolute
                        left-1/2
                        top-1/2
                        flex
                        h-32
                        w-32
                        -translate-x-1/2
                        -translate-y-1/2
                        items-center
                        justify-center
                        rounded-[35px]
                        border
                        border-[#59b5cd]/40
                        bg-[#59b5cd]/10
                        backdrop-blur-xl
                      "
                    >

                      <BrainCircuit
                        size={65}
                        className="text-[#59b5cd]"
                      />

                    </motion.div>


                    {/* orbit nodes */}

                    {[
                      {
                        top: "18%",
                        left: "20%",
                        icon: Database,
                      },

                      {
                        top: "20%",
                        right: "18%",
                        icon: Code2,
                      },

                      {
                        bottom: "18%",
                        left: "18%",
                        icon: BarChart3,
                      },

                      {
                        bottom: "18%",
                        right: "18%",
                        icon: Bot,
                      },
                    ].map((node, index) => {

                      const Icon = node.icon;

                      return (

                        <motion.div
                          key={index}
                          animate={{
                            y: [0, -8, 0],
                          }}
                          transition={{
                            duration: 3 + index,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                          style={{
                            top: node.top,
                            left: node.left,
                            right: node.right,
                            bottom: node.bottom,
                          }}
                          className="
                            absolute
                            flex
                            h-14
                            w-14
                            items-center
                            justify-center
                            rounded-2xl
                            border
                            border-white/10
                            bg-white/[0.06]
                            text-[#ecb32c]
                            backdrop-blur-xl
                          "
                        >

                          <Icon size={24} />

                        </motion.div>

                      );

                    })}


                    {/* bottom label */}

                    <div
                      className="
                        absolute
                        bottom-6
                        left-6
                        right-6
                        flex
                        items-center
                        justify-between
                        rounded-2xl
                        border
                        border-white/10
                        bg-black/30
                        p-4
                        backdrop-blur-xl
                      "
                    >

                      <div className="flex items-center gap-3">

                        <div
                          className="
                            flex
                            h-10
                            w-10
                            items-center
                            justify-center
                            rounded-xl
                            bg-[#59b5cd]
                            text-[#07141a]
                          "
                        >

                          <Cpu size={20} />

                        </div>

                        <div>

                          <p className="font-bold text-white">
                            AI Laboratory
                          </p>

                          <p className="text-xs text-white/40">
                            Data → Model → Intelligence
                          </p>

                        </div>

                      </div>


                      <div className="flex items-center gap-2">

                        <span className="h-2 w-2 animate-pulse rounded-full bg-green-400" />

                        <span className="text-[10px] text-white/40">
                          SYSTEM ONLINE
                        </span>

                      </div>

                    </div>

                  </div>

                </div>


                {/* Floating cards */}

                <motion.div
                  animate={{
                    y: [0, -10, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="
                    absolute
                    -bottom-5
                    -left-8
                    z-20
                    hidden
                    items-center
                    gap-3
                    rounded-2xl
                    bg-white
                    p-4
                    shadow-2xl
                    md:flex
                  "
                >

                  <div
                    className="
                      flex
                      h-11
                      w-11
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#59b5cd]/10
                    "
                  >

                    <Database
                      size={21}
                      className="text-[#076c93]"
                    />

                  </div>

                  <div>

                    <p className="font-bold text-[#07141a]">
                      Data → Intelligence
                    </p>

                    <p className="text-xs text-gray-500">
                      Learn the complete pipeline
                    </p>

                  </div>

                </motion.div>


                <motion.div
                  animate={{
                    y: [0, 10, 0],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="
                    absolute
                    -right-8
                    top-12
                    z-20
                    hidden
                    items-center
                    gap-3
                    rounded-2xl
                    bg-[#ecb32c]
                    p-4
                    shadow-xl
                    md:flex
                  "
                >

                  <Zap size={24} />

                  <div>

                    <p className="font-bold">
                      Build With AI
                    </p>

                    <p className="text-xs opacity-70">
                      Not just theory
                    </p>

                  </div>

                </motion.div>

              </motion.div>

            </div>

          </div>

        </section>


        {/* =====================================================
            SNAPSHOT
        ===================================================== */}

        <section className="relative z-20 -mt-12 px-6">

          <div className="mx-auto max-w-6xl">

            <div
              className="
                grid
                grid-cols-2
                overflow-hidden
                rounded-3xl
                border
                border-gray-100
                bg-white
                shadow-2xl
                md:grid-cols-4
              "
            >

              {[
                {
                  icon: Clock3,
                  title: "Practical",
                  text: "AI Learning",
                },

                {
                  icon: Code2,
                  title: "Python",
                  text: "Core Language",
                },

                {
                  icon: BrainCircuit,
                  title: "Machine Learning",
                  text: "Core Focus",
                },

                {
                  icon: Award,
                  title: "Certificate",
                  text: "Upon Completion",
                },
              ].map((item, index) => {

                const Icon = item.icon;

                return (

                  <div
                    key={index}
                    className="
                      flex
                      items-center
                      gap-4
                      border-b
                      border-gray-100
                      p-6
                      md:border-b-0
                      md:border-r
                      md:p-8
                      last:border-0
                    "
                  >

                    <div
                      className="
                        flex
                        h-11
                        w-11
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        bg-[#59b5cd]/10
                      "
                    >

                      <Icon
                        size={21}
                        className="text-[#076c93]"
                      />

                    </div>

                    <div>

                      <p className="text-lg font-bold">
                        {item.title}
                      </p>

                      <p className="text-sm text-gray-500">
                        {item.text}
                      </p>

                    </div>

                  </div>

                );

              })}

            </div>

          </div>

        </section>


        {/* =====================================================
            INTRO
        ===================================================== */}

        <section className="mx-auto max-w-7xl px-6 py-28">

          <motion.div
            variants={fadeUp}
            whileInView="visible"
            initial="hidden"
            viewport={{
              once: true,
            }}
            className="max-w-4xl"
          >

            <p
              className="
                mb-4
                text-sm
                font-bold
                uppercase
                tracking-[0.25em]
                text-[#076c93]
              "
            >
              Why Artificial Intelligence?
            </p>

            <h2
              className="
                text-4xl
                font-black
                leading-tight
                md:text-6xl
              "
            >

              Don't just use AI.

              <span className="block text-[#59b5cd]">
                Learn how to build it.
              </span>

            </h2>

            <p
              className="
                mt-7
                max-w-3xl
                text-lg
                leading-relaxed
                text-gray-600
              "
            >
              AI is transforming how businesses operate, how products
              are built and how people work. Our programme gives you
              the technical foundation to understand AI and the practical
              skills to build useful intelligent applications.
            </p>

          </motion.div>


          {/* Features */}

          <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-4">

            {features.map((feature, index) => {

              const Icon = feature.icon;

              return (

                <motion.div
                  key={index}
                  variants={fadeUp}
                  whileInView="visible"
                  initial="hidden"
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    delay: index * 0.1,
                  }}
                  whileHover={{
                    y: -6,
                  }}
                  className="
                    group
                    rounded-3xl
                    border
                    border-gray-100
                    bg-white
                    p-7
                    transition
                    hover:border-[#59b5cd]/30
                    hover:shadow-xl
                  "
                >

                  <div
                    className="
                      mb-6
                      flex
                      h-12
                      w-12
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#59b5cd]/10
                      transition
                      group-hover:bg-[#ecb32c]
                    "
                  >

                    <Icon size={23} />

                  </div>

                  <h3 className="text-xl font-bold">
                    {feature.title}
                  </h3>

                  <p className="mt-3 text-sm leading-relaxed text-gray-500">
                    {feature.text}
                  </p>

                </motion.div>

              );

            })}

          </div>

        </section>


        {/* =====================================================
            AI PIPELINE
        ===================================================== */}

        <section className="relative overflow-hidden bg-[#07141a]">

          <div
            className="
              absolute
              -left-40
              top-0
              h-[500px]
              w-[500px]
              rounded-full
              bg-[#59b5cd]/10
              blur-[130px]
            "
          />

          <div
            className="
              absolute
              -right-40
              bottom-0
              h-[500px]
              w-[500px]
              rounded-full
              bg-[#ecb32c]/10
              blur-[130px]
            "
          />


          <div className="relative mx-auto max-w-7xl px-6 py-28">

            <div className="grid items-center gap-16 lg:grid-cols-2">

              <div>

                <p
                  className="
                    text-sm
                    font-bold
                    uppercase
                    tracking-[0.25em]
                    text-[#ecb32c]
                  "
                >
                  The AI Journey
                </p>

                <h2
                  className="
                    mt-5
                    text-4xl
                    font-black
                    leading-tight
                    text-white
                    md:text-6xl
                  "
                >

                  From raw data

                  <span className="text-[#59b5cd]">
                    {" "}to intelligence.
                  </span>

                </h2>

                <p
                  className="
                    mt-7
                    max-w-xl
                    text-lg
                    leading-relaxed
                    text-white/55
                  "
                >
                  Understand the complete lifecycle of an AI
                  solution — from collecting and preparing data
                  to training models and deploying intelligent
                  applications.
                </p>


                <div className="mt-9 space-y-4">

                  {[
                    "Collect & understand data",
                    "Prepare and transform datasets",
                    "Train machine learning models",
                    "Evaluate and improve performance",
                    "Deploy intelligent applications",
                  ].map((item, index) => (

                    <div
                      key={index}
                      className="flex items-center gap-3 text-white"
                    >

                      <CheckCircle2
                        size={20}
                        className="text-[#ecb32c]"
                      />

                      {item}

                    </div>

                  ))}

                </div>

              </div>


              {/* Pipeline visual */}

              <div className="relative">

                <div
                  className="
                    rounded-[30px]
                    border
                    border-white/10
                    bg-white/[0.04]
                    p-6
                    backdrop-blur-xl
                  "
                >

                  <div className="space-y-3">

                    {[
                      {
                        icon: Database,
                        title: "DATA",
                        text: "Collect & prepare",
                      },

                      {
                        icon: BarChart3,
                        title: "ANALYSIS",
                        text: "Discover patterns",
                      },

                      {
                        icon: BrainCircuit,
                        title: "MODEL",
                        text: "Train intelligence",
                      },

                      {
                        icon: Bot,
                        title: "APPLICATION",
                        text: "Build useful AI",
                      },
                    ].map((item, index) => {

                      const Icon = item.icon;

                      return (

                        <div key={index}>

                          <motion.div
                            whileHover={{
                              x: 6,
                            }}
                            className="
                              flex
                              items-center
                              gap-4
                              rounded-2xl
                              border
                              border-white/10
                              bg-white/[0.04]
                              p-4
                            "
                          >

                            <div
                              className="
                                flex
                                h-12
                                w-12
                                items-center
                                justify-center
                                rounded-xl
                                bg-[#59b5cd]/10
                                text-[#59b5cd]
                              "
                            >

                              <Icon size={23} />

                            </div>

                            <div>

                              <p
                                className="
                                  text-[9px]
                                  font-black
                                  uppercase
                                  tracking-[0.2em]
                                  text-[#ecb32c]
                                "
                              >
                                {item.title}
                              </p>

                              <p className="mt-1 font-semibold text-white">
                                {item.text}
                              </p>

                            </div>

                          </motion.div>


                          {index < 3 && (

                            <div className="ml-10 h-5 w-px bg-[#59b5cd]/30" />

                          )}

                        </div>

                      );

                    })}

                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CURRICULUM
        ===================================================== */}

        <section
          id="curriculum"
          className="mx-auto max-w-7xl px-6 py-28"
        >

          <div className="mx-auto max-w-3xl text-center">

            <p
              className="
                text-sm
                font-bold
                uppercase
                tracking-[0.25em]
                text-[#076c93]
              "
            >
              What You'll Learn
            </p>

            <h2
              className="
                mt-4
                text-4xl
                font-black
                md:text-6xl
              "
            >

              Your AI

              <span className="text-[#ecb32c]">
                {" "}toolkit.
              </span>

            </h2>

            <p className="mt-6 text-lg text-gray-500">
              A practical curriculum covering the foundations,
              tools and technologies behind modern artificial intelligence.
            </p>

          </div>


          <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">

            {curriculum.map((item, index) => (

              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 15,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  delay: (index % 6) * 0.04,
                }}
                whileHover={{
                  y: -4,
                }}
                className="
                  flex
                  items-center
                  gap-4
                  rounded-2xl
                  border
                  border-gray-100
                  bg-[#f8fbfc]
                  p-5
                  transition
                  hover:border-[#59b5cd]/40
                  hover:bg-white
                  hover:shadow-lg
                "
              >

                <div
                  className="
                    flex
                    h-9
                    w-9
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-[#59b5cd]/10
                  "
                >

                  <CheckCircle2
                    size={18}
                    className="text-[#076c93]"
                  />

                </div>

                <span className="font-semibold text-gray-800">
                  {item}
                </span>

              </motion.div>

            ))}

          </div>

        </section>


        {/* =====================================================
            TECHNOLOGY STACK
        ===================================================== */}

        <section className="bg-[#f6fafb]">

          <div className="mx-auto max-w-7xl px-6 py-24">

            <div className="grid items-center gap-12 lg:grid-cols-2">

              <div>

                <p
                  className="
                    text-sm
                    font-bold
                    uppercase
                    tracking-[0.25em]
                    text-[#076c93]
                  "
                >
                  Tools You'll Work With
                </p>

                <h2
                  className="
                    mt-4
                    text-4xl
                    font-black
                    md:text-5xl
                  "
                >

                  Learn the

                  <span className="text-[#59b5cd]">
                    {" "}modern AI stack.
                  </span>

                </h2>

                <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600">
                  Get hands-on experience with technologies
                  commonly used to analyse data, train models
                  and build AI-powered applications.
                </p>

              </div>


              <div className="flex flex-wrap gap-3">

                {technologies.map((tech, index) => (

                  <motion.div
                    key={tech}
                    initial={{
                      opacity: 0,
                      scale: 0.9,
                    }}
                    whileInView={{
                      opacity: 1,
                      scale: 1,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      delay: index * 0.04,
                    }}
                    whileHover={{
                      y: -3,
                    }}
                    className="
                      rounded-xl
                      border
                      border-gray-200
                      bg-white
                      px-4
                      py-3
                      text-sm
                      font-semibold
                      text-gray-700
                      shadow-sm
                      transition
                      hover:border-[#59b5cd]/40
                      hover:shadow-md
                    "
                  >

                    {tech}

                  </motion.div>

                ))}

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CAREER PATHS
        ===================================================== */}

        <section>

          <div className="mx-auto max-w-7xl px-6 py-28">

            <div className="grid items-center gap-16 lg:grid-cols-2">

              <div>

                <p
                  className="
                    text-sm
                    font-bold
                    uppercase
                    tracking-[0.25em]
                    text-[#076c93]
                  "
                >
                  Where It Can Take You
                </p>

                <h2
                  className="
                    mt-4
                    text-4xl
                    font-black
                    md:text-5xl
                  "
                >

                  One AI foundation.

                  <span className="block text-[#59b5cd]">
                    Multiple possibilities.
                  </span>

                </h2>

                <p
                  className="
                    mt-6
                    max-w-xl
                    text-lg
                    leading-relaxed
                    text-gray-600
                  "
                >
                  AI skills can take you across software,
                  finance, healthcare, logistics, research,
                  automation, data and product development.
                </p>


                <Link to="/get-started">

                  <motion.button
                    whileHover={{
                      scale: 1.03,
                    }}
                    className="
                      mt-8
                      flex
                      items-center
                      gap-3
                      rounded-xl
                      bg-[#07141a]
                      px-7
                      py-4
                      font-bold
                      text-white
                      transition
                      hover:bg-[#076c93]
                    "
                  >

                    Begin Your AI Journey

                    <ArrowRight size={19} />

                  </motion.button>

                </Link>

              </div>


              <div className="grid gap-4 sm:grid-cols-2">

                {careerPaths.map((career, index) => {

                  const Icon = career.icon;

                  return (

                    <motion.div
                      key={index}
                      whileHover={{
                        y: -5,
                      }}
                      className="
                        rounded-2xl
                        border
                        border-gray-100
                        bg-white
                        p-6
                        shadow-sm
                        transition
                        hover:shadow-xl
                      "
                    >

                      <div
                        className="
                          mb-5
                          flex
                          h-10
                          w-10
                          items-center
                          justify-center
                          rounded-xl
                          bg-[#ecb32c]/15
                        "
                      >

                        <Icon
                          size={20}
                          className="text-[#a87500]"
                        />

                      </div>

                      <h3 className="font-bold">
                        {career.title}
                      </h3>

                      <p className="mt-2 text-xs text-gray-400">
                        Career pathway
                      </p>

                    </motion.div>

                  );

                })}

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            PROGRAMME DETAILS
        ===================================================== */}

      <section className="relative overflow-hidden bg-[#f8fafb]">

  {/* subtle background glow */}
  <div className="pointer-events-none absolute -left-32 top-20 h-72 w-72 rounded-full bg-[#59b5cd]/10 blur-[100px]" />
  <div className="pointer-events-none absolute -right-32 bottom-0 h-72 w-72 rounded-full bg-[#ecb32c]/10 blur-[100px]" />

  <div className="relative mx-auto max-w-6xl px-5 py-5 sm:px-6 sm:py-10">

    {/* HEADER */}
    <div className="mb-10 flex flex-col items-center text-center">

      <div
        className="
          mb-4
          inline-flex
          items-center
          gap-2
          rounded-full
          border
          border-[#59b5cd]/20
          bg-white
          px-3.5
          py-1.5
          shadow-sm
        "
      >
        <span className="h-1.5 w-1.5 rounded-full bg-[#59b5cd]" />

        <span
          className="
            text-[9px]
            font-black
            uppercase
            tracking-[0.22em]
            text-[#076c93]
          "
        >
          Program Investments
        </span>
      </div>


      <h2
        className="
          max-w-2xl
          text-3xl
          font-black
          tracking-tight
          text-[#07141a]
          sm:text-4xl
          md:text-5xl
        "
      >
        Start{" "}
        <span className="text-[#ecb32c]">
          building with AI?
        </span>
      </h2>



    </div>


    {/* COURSE CARD */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="
        mx-auto
        max-w-5xl
        overflow-hidden
        rounded-[26px]
        border
      "
    >

      {/* accent line */}
      <div
        className="
          h-[3px]
          w-full
          bg-gradient-to-r
          from-[#59b5cd]
          via-[#ecb32c]
          to-[#CF385D]
        "
      />

      <div className="p-4 sm:p-6 md:p-8">

        <CourseDescription />

      </div>

    </motion.div>

  </div>

</section>


        {/* =====================================================
            FINAL CTA
        ===================================================== */}

        <section
          className="
            relative
            overflow-hidden
            bg-[#07141a]
          "
        >

          <div
            className="
              absolute
              -left-40
              -top-40
              h-[500px]
              w-[500px]
              rounded-full
              bg-[#59b5cd]/15
              blur-[140px]
            "
          />

          <div
            className="
              absolute
              -bottom-40
              -right-40
              h-[500px]
              w-[500px]
              rounded-full
              bg-[#ecb32c]/10
              blur-[140px]
            "
          />


          <div
            className="
              relative
              mx-auto
              max-w-5xl
              px-6
              py-28
              text-center
            "
          >

            <div
              className="
                mb-6
                inline-flex
                items-center
                gap-2
                font-semibold
                text-[#ecb32c]
              "
            >

              <BrainCircuit size={19} />

              The future is intelligent.

            </div>


            <h2
              className="
                text-4xl
                font-black
                leading-tight
                text-white
                md:text-6xl
              "
            >

              Don't watch the AI revolution.

              <span className="block text-[#59b5cd]">
                Build part of it.
              </span>

            </h2>


            <p
              className="
                mx-auto
                mt-6
                max-w-2xl
                text-lg
                text-white/50
              "
            >
              Start developing the technical foundation,
              practical experience and confidence to build
              intelligent solutions.
            </p>


            <div className="mt-9 flex flex-wrap justify-center gap-4">

              <Link to="/get-started">

                <motion.button
                  whileHover={{
                    scale: 1.03,
                  }}
                  whileTap={{
                    scale: 0.97,
                  }}
                  className="
                    flex
                    items-center
                    gap-3
                    rounded-xl
                    bg-[#ecb32c]
                    px-8
                    py-4
                    font-bold
                    text-[#07141a]
                    shadow-xl
                  "
                >

                  Start Your AI Journey

                  <ArrowRight size={20} />

                </motion.button>

              </Link>


              <a href="#curriculum">

                <button
                  className="
                    rounded-xl
                    border
                    border-white/15
                    bg-white/5
                    px-8
                    py-4
                    font-semibold
                    text-white
                    transition
                    hover:bg-white/10
                  "
                >

                  Explore Curriculum

                </button>

              </a>

            </div>

          </div>

        </section>

      </div>

    </Layout>
  );
};

export default ArtificialIntelligence;